// Composer Studio — 상시 캡처 textarea(셀 타이핑 자동 편집, IME 안전)
//
// 스프레드시트 정석(Google Sheets·ag-Grid)의 "hidden capture input" 패턴이다. 보이지 않는
// 단일 <textarea>를 항상 DOM에 두고, 셀 선택(탐색) 상태에서 포커스를 유지한다. IME(한글 등)는
// input/textarea에 포커스가 있어야 첫 자모부터 조합되므로, div(셀 컨테이너) 포커스로는 첫 자모가
// 영문 raw key로 깨진다(1차 구현 49ccf874a의 실패 원인). 이 textarea에 포커스를 두면 처음부터
// 그 textarea에서 조합돼 첫 자모가 보존된다.
//
//   - 탐색 모드(editing=false): off-screen 1px·opacity:0 홀더. 화살표/F2/Ctrl+* 등 기능키는
//     컨테이너로 위임(stopPropagation 안 함). Enter/Tab만 기본동작(줄바꿈/포커스이동) 차단.
//   - 편집 모드(editing=true): 같은 textarea를 선택 셀 위치/폰트/정렬로 보이게 전환(조합 이관
//     없음 — 동일 DOM이라 첫 자모/조합 유지). 기존 값 덮어쓰기(빈 값에서 시작). Enter=커밋,
//     Shift+Enter=줄바꿈, Esc=취소, Tab=커밋+다음 셀, blur=커밋.
//
// 편집 진입은 캡처 textarea가 직접 감지한다(컨테이너 keydown 아님):
//   - 영문/숫자/기호: native `beforeinput`(inputType=insertText, data 있음).
//   - 한글 등 IME: native `compositionstart`.
// onTypingStart()가 true(정적 텍스트 셀=편집 대상)를 반환하면 EditorPage가 편집 모드로 승격한다.

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import type { Rect } from './canvasRect';
import { cssEscape, rectRelativeTo } from './canvasRect';
import { justifyForVerticalAlign, type CloseReason } from './InlineTextEditor';

/** 편집 대상 셀(캡처 editor는 셀만 다룬다 — 노드 text 편집은 기존 InlineTextEditor). */
export type CaptureCellTarget = { tableId: string; row: number; col: number };

/** 셀 DOM selector (`data-composer-cell="tableId::row::col"`). */
function cellSelector(t: CaptureCellTarget): string {
  return `[data-composer-cell="${cssEscape(`${t.tableId}::${t.row}::${t.col}`)}"]`;
}

type FontStyle = {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  textAlign?: string;
  verticalAlign?: string;
  color?: string;
  lineHeight?: string;
  padding?: string;
};

export function CellCaptureEditor({
  textareaRef,
  canvas,
  zoom = 1,
  editing,
  target,
  onTypingStart,
  onCommit,
  onSaveShortcut,
  onCancel,
  onPreview,
  onTab,
  onClipboardCopy,
  onClipboardCut,
  onClipboardPaste,
}: {
  /** 캡처 textarea ref — EditorPage가 셀 선택 시 .focus()로 포커스를 유지한다. */
  textareaRef: RefObject<HTMLTextAreaElement>;
  canvas: HTMLElement | null;
  /** 캔버스 줌 배율. 편집 모드에서 폰트/크기를 스케일된 셀과 맞춘다(WYSIWYG). */
  zoom?: number;
  /** 편집 모드 여부(false=탐색/캡처 모드). */
  editing: boolean;
  /** 편집 중인 셀(editing=true일 때). 탐색 모드면 null. */
  target: CaptureCellTarget | null;
  /**
   * 탐색 모드에서 첫 글자 입력 감지 시 호출. EditorPage가 현재 anchor 셀이 정적 텍스트 셀이면
   * 편집 모드로 승격하고 true를 반환한다(아니면 false — 입력 무시). 동일 textarea라 첫 자모 보존.
   */
  onTypingStart: () => boolean;
  /** 커밋 — 현재 textarea 값으로 적용 후 닫기. */
  onCommit: (value: string, reason: CloseReason) => void;
  /**
   * 편집 중 Ctrl/Cmd+S — 현재 값을 먼저 커밋한 뒤 앱 저장을 트리거한다. 편집 중에는 이 컴포넌트가
   * keydown을 stopPropagation하므로 window의 저장 단축키가 도달하지 못해(브라우저 "페이지 저장"
   * 대화상자가 뜸) 여기서 직접 처리한다. EditorPage가 지연 저장 래퍼(방금 커밋한 값 반영)를 넘긴다.
   */
  onSaveShortcut?: () => void;
  /** 취소 — 변경 없이 닫기. */
  onCancel: (reason: CloseReason) => void;
  /** 입력 live 반영(편집 모드, 매 입력마다). */
  onPreview: (value: string) => void;
  /** Tab — 현재 값 커밋 후 다음/이전 셀로 이동. */
  onTab: (dir: 'next' | 'prev') => void;
  /**
   * 탐색 모드 복사(Ctrl+C). EditorPage가 선택 셀 범위를 TSV로 만들어 clipboardData에 넣고 true를
   * 반환하면 기본 동작(textarea 텍스트 복사)을 막는다. 편집 모드에선 호출 안 함(기본 텍스트 복붙 유지).
   */
  onClipboardCopy?: (clipboardData: DataTransfer) => boolean;
  /** 탐색 모드 잘라내기(Ctrl+X) — 복사 + 원본 셀 text 비우기. true 반환 시 preventDefault. */
  onClipboardCut?: (clipboardData: DataTransfer) => boolean;
  /** 탐색 모드 붙여넣기(Ctrl+V) — text/plain을 받아 anchor부터 셀 채우기. true 반환 시 preventDefault. */
  onClipboardPaste?: (text: string) => boolean;
}) {
  const [rect, setRect] = useState<Rect | null>(null);
  const [fontStyle, setFontStyle] = useState<FontStyle>({});

  // 콜백/모드는 안정 ref로 보관 — native 리스너·measure effect가 stale 클로저 없이 최신값을 읽고,
  // deps에 콜백을 넣어 measure 루프가 재발하는 것을 막는다(InlineTextEditor와 동일 전략).
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const onTypingStartRef = useRef(onTypingStart);
  onTypingStartRef.current = onTypingStart;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const onSaveShortcutRef = useRef(onSaveShortcut);
  onSaveShortcutRef.current = onSaveShortcut;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const onLiveRef = useRef(onPreview);
  onLiveRef.current = onPreview;
  const onTabRef = useRef(onTab);
  onTabRef.current = onTab;
  const onCopyRef = useRef(onClipboardCopy);
  onCopyRef.current = onClipboardCopy;
  const onCutRef = useRef(onClipboardCut);
  onCutRef.current = onClipboardCut;
  const onPasteRef = useRef(onClipboardPaste);
  onPasteRef.current = onClipboardPaste;

  // 커밋/취소 중복(예: Enter 후 blur) 1회 가드. 편집 시작마다 리셋.
  const closedRef = useRef(false);

  const targetTableId = target?.tableId;
  const targetRow = target?.row;
  const targetCol = target?.col;

  // 편집 시작/종료 — closedRef 리셋(시작) / 값 초기화(종료=탐색 모드 진입, 다음 타이핑 깨끗이 시작).
  useEffect(() => {
    if (editing) {
      closedRef.current = false;
    } else {
      const el = textareaRef.current;
      if (el) {
        el.value = '';
        el.style.height = 'auto';
      }
    }
  }, [editing, textareaRef]);

  // native 입력 감지(탐색 모드에서 편집 승격) — React onBeforeInput은 inputType 신뢰성이 낮아 native로.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const onBeforeInput = (e: InputEvent) => {
      if (editingRef.current) return; // 편집 중엔 일반 입력
      // 영문/숫자/기호(IME 아님). insertText + data 있을 때만.
      if (e.inputType === 'insertText' && e.data != null && e.data !== '') {
        const entered = onTypingStartRef.current();
        if (!entered) {
          // 비대상 셀(바인딩/content 셀) — 입력 무시(스트레이 방지).
          e.preventDefault();
        }
        // entered=true면 막지 않음 — 그 문자가 textarea에 그대로 들어가 편집 시작값이 된다.
      }
    };
    const onCompositionStart = () => {
      if (editingRef.current) return; // 편집 중엔 일반 조합
      const entered = onTypingStartRef.current();
      if (!entered) {
        // 비대상 셀 — 조합은 막을 수 없으나(off-screen 진행), 끝나면 값을 비워 스트레이를 제거.
        const clear = () => {
          const t = textareaRef.current;
          if (t) t.value = '';
          el.removeEventListener('compositionend', clear);
        };
        el.addEventListener('compositionend', clear);
      }
    };
    el.addEventListener('beforeinput', onBeforeInput);
    el.addEventListener('compositionstart', onCompositionStart);
    return () => {
      el.removeEventListener('beforeinput', onBeforeInput);
      el.removeEventListener('compositionstart', onCompositionStart);
    };
  }, [textareaRef]);

  // 편집 모드 — 대상 셀 rect + computed style 측정(WYSIWYG). ResizeObserver로 셀 변화를 추적.
  useLayoutEffect(() => {
    if (!editing || targetTableId === undefined || targetRow === undefined || targetCol === undefined || !canvas) {
      setRect(null);
      return;
    }
    const sel = cellSelector({ tableId: targetTableId, row: targetRow, col: targetCol });
    const measure = () => {
      const el = canvas.querySelector<HTMLElement>(sel);
      if (!el) {
        // 대상 소멸 → 편집 종료(프로그램적, 포커스 복원 X).
        if (!closedRef.current) {
          closedRef.current = true;
          onCancelRef.current('auto');
        }
        return;
      }
      setRect(rectRelativeTo(canvas, el));
      const cs = window.getComputedStyle(el);
      setFontStyle({
        fontFamily: cs.fontFamily,
        fontSize: cs.fontSize,
        fontWeight: cs.fontWeight,
        fontStyle: cs.fontStyle,
        textDecoration: cs.textDecorationLine || undefined,
        textAlign: cs.textAlign,
        verticalAlign: cs.verticalAlign,
        color: cs.color,
        lineHeight: cs.lineHeight,
        padding: cs.padding,
      });
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const el = canvas.querySelector<HTMLElement>(sel);
    const ro = new ResizeObserver(() => measure());
    if (el) ro.observe(el);
    ro.observe(canvas);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [editing, targetTableId, targetRow, targetCol, canvas, zoom]);

  // textarea를 콘텐츠 높이로(auto-grow). wrapper가 셀 높이를 갖고 수직 정렬을 담당한다.
  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const commit = (reason: CloseReason) => {
    if (closedRef.current) return;
    closedRef.current = true;
    onCommitRef.current(textareaRef.current?.value ?? '', reason);
  };
  const cancel = (reason: CloseReason) => {
    if (closedRef.current) return;
    closedRef.current = true;
    onCancelRef.current(reason);
  };

  const wrapperStyle: CSSProperties =
    editing && rect
      ? {
          // 편집 모드 — 선택 셀 위에 절대배치(InlineTextEditor wrapper와 동일 규약).
          position: 'absolute',
          top: rect.top,
          left: rect.left,
          width: rect.width / zoom,
          height: rect.height / zoom,
          transform: zoom === 1 ? undefined : `scale(${zoom})`,
          transformOrigin: 'top left',
          margin: 0,
          boxSizing: 'border-box',
          boxShadow: 'inset 0 0 0 2px #4f46e5', // brand-600
          overflow: 'hidden',
          background: '#ffffff',
          zIndex: 40,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: justifyForVerticalAlign(fontStyle.verticalAlign),
        }
      : {
          // 탐색(캡처) 모드 — 보이지 않는 1px 홀더(opacity:0). off-screen(-9999px) 대신 0,0·1px라
          // 스크롤 영역에 영향이 없다. opacity:0이라도 포커스/IME는 동작한다(visibility:hidden·
          // display:none은 포커스 불가라 금지). pointer-events:none으로 클릭을 가리지 않는다.
          position: 'absolute',
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          opacity: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          margin: 0,
          zIndex: 0,
        };

  const textareaStyle: CSSProperties = editing
    ? {
        width: '100%',
        height: 'auto',
        margin: 0,
        boxSizing: 'border-box',
        border: 'none',
        outline: 'none',
        resize: 'none',
        overflow: 'hidden',
        background: 'transparent',
        userSelect: 'text',
        // WYSIWYG — 대상 셀의 폰트/수평 정렬 복사.
        fontFamily: fontStyle.fontFamily,
        fontSize: fontStyle.fontSize,
        fontWeight: fontStyle.fontWeight,
        fontStyle: fontStyle.fontStyle,
        textDecoration: fontStyle.textDecoration,
        textAlign: fontStyle.textAlign as CSSProperties['textAlign'],
        color: fontStyle.color,
        lineHeight: fontStyle.lineHeight,
        padding: fontStyle.padding,
      }
    : {
        // 탐색 모드 — 보이지 않는 캡처용. 폰트 무관(안 보임).
        width: '100%',
        height: '100%',
        margin: 0,
        border: 'none',
        outline: 'none',
        resize: 'none',
        overflow: 'hidden',
        background: 'transparent',
        opacity: 0,
      };

  return (
    <div style={wrapperStyle}>
      <textarea
        ref={textareaRef}
        // 비제어(uncontrolled) — 값은 IME/입력이 직접 채우고, 모드 전환 시 effect가 초기화한다.
        // defaultValue/value를 주면 조합 중 React가 값을 덮어 첫 자모가 깨질 수 있어 둘 다 미사용.
        data-composer-capture="true"
        rows={1}
        aria-label="셀 텍스트 입력(캡처)"
        onChange={(e) => {
          autoGrow();
          if (editingRef.current) onLiveRef.current(e.currentTarget.value);
        }}
        onKeyDown={(e) => {
          if (editingRef.current) {
            // 편집 모드 — 컨테이너 단축키로 새지 않게 차단 + 종료 키 처리.
            e.stopPropagation();
            // Ctrl/Cmd+S — 현재 값 커밋 후 저장(브라우저 "페이지 저장" 대화상자 차단). stopPropagation을
            // 유지하므로 window 저장 핸들러엔 도달하지 않는다 → 여기서 직접 커밋+저장을 호출한다.
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 's') {
              e.preventDefault();
              commit('keyboard');
              onSaveShortcutRef.current?.();
              return;
            }
            // Ctrl/Cmd+D — 편집 중 노드 복제는 의미 없으므로 삼킨다(브라우저 북마크 대화상자 차단).
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'd') {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              commit('keyboard');
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel('keyboard');
            } else if (e.key === 'Tab') {
              // 현재 값 커밋 후 다음/이전 셀로(Shift+Tab=이전).
              e.preventDefault();
              commit('keyboard');
              onTabRef.current(e.shiftKey ? 'prev' : 'next');
            }
            // Shift+Enter = 기본 줄바꿈.
            return;
          }
          // 탐색 모드 — 화살표/F2/Ctrl+* 등은 컨테이너 핸들러로 위임(stopPropagation 안 함).
          // Enter/Tab만 textarea 기본동작(줄바꿈/포커스 이동)을 막아 포커스·값을 보존한다.
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
          }
        }}
        onCopy={(e) => {
          // 탐색 모드에서만 셀 단위 복사. 편집 모드면 textarea 기본(텍스트) 복사 유지.
          if (editingRef.current) return;
          if (onCopyRef.current?.(e.clipboardData)) e.preventDefault();
        }}
        onCut={(e) => {
          if (editingRef.current) return;
          if (onCutRef.current?.(e.clipboardData)) e.preventDefault();
        }}
        onPaste={(e) => {
          if (editingRef.current) return;
          const text = e.clipboardData.getData('text/plain');
          if (onPasteRef.current?.(text)) e.preventDefault();
        }}
        onBlur={() => {
          if (editingRef.current) commit('blur');
        }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={textareaStyle}
      />
    </div>
  );
}
